import { transient, autoinject, bindable, computedFrom } from "aurelia-framework";
import { IAssignEntity } from "../../../models/basics/basics.models";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { AssignmentService } from "../../../services/basics/assignment.service";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { Subscription, EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { ValidationActivation, ValidationTypeEnum } from "../../../client-validation-system/validations.models";
import { ClientSideValidator, EntityValidationsService } from "../../../client-validation-system/validations-service";
import { LookupValue } from "../../../models/lookups/lookup.models";
import { CustomerService } from "../../../services/modules/customer-client.service";
import { Guid } from "../../../utilities/guid";
import { Reflection } from "../../../utilities/reflection";

@autoinject
@transient()
export class Assignment {

    @bindable
    public entityType: number;

    @bindable
    public entityId: number;

    @bindable
    public item: any;

    @bindable
    public canSetUnassignedAfterAssignment: boolean = true;

    @bindable
    public canReassign: boolean = true;

    @bindable
    public canAssignToMe: (() => boolean);

    @bindable
    public disabled: boolean = false;

    public isHostedCustomer: boolean = false;
    public isAssigning: boolean = false;
    public assigningTo: number;

    public assignedEntity: IAssignEntity;
    public isMenuOpen: boolean;
    public canAssign: boolean = true;
    public assignmentComment: string;
    private validator: ClientSideValidator;
    private previousAssignment: number;

    constructor(private userService: UserClientService,
        private eventAggregator: EventAggregator,
        private assignmentService: AssignmentService, private entityValidationsService: EntityValidationsService,
        private notificationService: NotificationClientService, private customerService: CustomerService) {
        this.customerService.isHostedCustomer()
            .then(x => {
                this.isHostedCustomer = x;
            })
            .catch(e => {
                console.warn("Error getting customer is hosted");
                console.warn(e);
                this.isHostedCustomer = false;
            });
    }

    assignmentDropdownClick($event) {
        if ($event)
            $event.stopPropagation();
    }

    _subscriptionToDocumentClickEvent: Subscription;

    attached() {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup);
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    closePopup = (x) => {
        if (x && x.source == 'document') {
            this.isMenuOpen = false;
            this.assigningTo = this.previousAssignment;
        }
    };

    async reload() {
        const promise = await this.assignmentService.get(this.entityId, this.entityType);
        const assignedEntity = promise.assignedEntity;
        this.assignedEntity = assignedEntity;
        this.assigningTo = assignedEntity? assignedEntity.assignedTo: null;
        this.previousAssignment = assignedEntity? assignedEntity.assignedTo: null;

        if (!this.assignedEntity) {
            this.assignedEntity = {
                assignedTo: null,
                identifier: Guid.generateGuid()
            };
        }

        const rules = {
            activation: ValidationActivation.Always,
            entityName: "Assignment",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Comments are required",
                parameters: null, property: "assignmentComment",
                validationType: ValidationTypeEnum.Required
            }]
        };
        this.validator = this.entityValidationsService.getValidator(this, rules);
        return this.assignedEntity;
    }

    entityTypeChanged() {
        if (this.entityType && this.entityId) {
            this.reload();
        }
    }

    entityIdChanged() {
        if (this.entityType && this.entityId) {
            this.reload();
        }
    }

    itemChanged() {
        if (this.entityType && this.entityId) {
            this.reload();
        }
    }

    async save(userId: number) {
        if (this.isAssigning) return;

        this.isAssigning = true;
        try {
            
            const result = await this.assignmentService.save(this.entityId, this.entityType, userId, this.assignmentComment, this.item.assignedUserId);
            
            this.assignedEntity.assignmentComment = '';
            this.assignedEntity.assignedTo = result.assignedTo;
            this.item.assignedUserId = result.assignedTo;
            this.assignmentComment = '';
            this.isMenuOpen = false;
            this.isAssigning = false;
            
            if (result.assignmentMismatch) {
                this.notificationService.show(
                    "Assignment not updated", 
                    "There has been a mismatch in the assignment's current status. This may be the result of an updated assignment at the same time as another user.", NotificationType.Warning);
            }
            else {
                this.notificationService.show("Assignment Updated Successfully", "", NotificationType.Success);
            }

            await this.reload();
        } catch (e) {
            this.isAssigning = false;
            this.notificationService.show("Assignment update failed", "", NotificationType.Error);
            throw e;
        }
    }

    cancel() {
        this.assignedEntity.assignedTo = this.previousAssignment;
        this.assigningTo = this.previousAssignment;
        this.isMenuOpen = false;
    }

    async submit() {
        const result = await this.validator.validate();
        if (result.valid) {
            this.isMenuOpen = false;
            await this.assignToUser(this.assigningTo);
        }
    }

    async unassign() {
        if (!this.disabled) {
            await this.assignToUser(null);
        }
    }

    async assignToMe() {
        if (!this.disabled) {
            await this.assignToUser(this.userService.currentUser.id);
        }
    }

    async assignToUser(userId: number) {
        if (userId == this.userService.currentUser.id) {
            this.assignmentComment = '';

            const canAssignToMe = !this.canAssignToMe || this.canAssignToMe();
            if (!canAssignToMe) return;
        }

        if (userId != this.item.assignedUserId) { // only save if different
            await this.save(userId);
        }
    }
   
    @computedFrom(Reflection.getPropertyPath<Assignment>(z => z.assignedEntity.assignedTo))  
    get isUnassigned(): boolean {
        return !this.assignedEntity.assignedTo;
    }

    @computedFrom(Reflection.getPropertyPath<Assignment>(z => z.isUnassigned), Reflection.getPropertyPath<Assignment>(z => z.canSetUnassignedAfterAssignment))  
    get canSetAsUnassigned(): boolean {
        if (this.isUnassigned) return true;

        if (!this.isUnassigned && !this.canSetUnassignedAfterAssignment) return false;

        return true;
    }

    @computedFrom(Reflection.getPropertyPath<Assignment>(z => z.assignedEntity.assignedTo),
        Reflection.getPropertyPath<Assignment>(z => z.userService.currentUser.id))  
    get isAssignedToMe(): boolean {
        return this.assignedEntity.assignedTo == this.userService.currentUser.id;
    }

    @computedFrom(Reflection.getPropertyPath<Assignment>(z => z.assigningTo),
        Reflection.getPropertyPath<Assignment>(z => z.userService.currentUser.id))  
    get isAssigningToMe(): boolean {
        return this.assigningTo == this.userService.currentUser.id;
    }

    toggleAssignMenu($event) {
        $event.stopPropagation();
        if(!this.disabled) {
            this.isMenuOpen = !this.isMenuOpen;
        }
    }

    sortUsers = (users: LookupValue[]) => {
        return users.sort((a, b) => {
            const nameA = a.name.toUpperCase(); // ignore upper and lowercase
            const nameB = b.name.toUpperCase(); // ignore upper and lowercase
            if (nameA > nameB) return 1;
            if (nameA < nameB) return -1;
            return 0;
        });
    }
}